import { stories } from './cardData';

export const anthropic = {
  currentStoryId: '2',
  stories: stories.filter((story) => story.id !== '2'),
  heroContent: {
    title: 'Anthropic Identity Builds a Global IAM Force',
    subtitle:
      'Unifying value as the central point of the continuous business lifecycle',
    description:
      'Anthropic Identity is an Identity and Access Management (IAM) services firm specializing in People, Process, and Technology modernization of IAM Programs. Through their signature Outcome-Oriented approach with tailored advisory, implementation, and managed services, Anthropic Identity helps organizations break down identity silos and align IAM strategies with business objectives. By delivering data-driven assessments and customized solutions, they empower businesses to enhance security, improve user experiences, and drive growth.',
    buttonText: 'More about Anthropic',
    buttonLink: 'https://www.anthropicidentity.com/',
    image: require('@/assets/img/anthropic-cover.svg'),
    navIndicator: [
      { text: 'Home', icon: 'fas fa-home', link: '/' },
      { text: 'Customer Stories', link: '/customers' },
      { text: 'Anthropic Identity Builds a Global IAM Force', link: '' },
    ],
  },
  tocSections: [
    { title: 'Key Stats', link: '#key-stats' },
    { title: 'The Challenge', link: '#the-challenge' },
    {
      title: 'The Solution: DIGU’s Staff Augmentation Service',
      link: '#the-solution',
    },
    { title: 'The Results', link: '#the-results' },
    {
      title: 'Impact Beyond Business: Empowering Nepal’s Tech Ecosystem',
      link: '#impact',
    },
    { title: 'Talent Testimonial', link: '#testimonial' },
    { title: 'Founder Testimonial', link: '#founder-testimonial' },
  ],
  keyMetrics: [
    { label: 'Service', value: 'Staff Augmentation', color: '#FFD1D1' },
    { label: 'Time to Hire', value: '2 weeks', color: '#D1E5FF' },
    { label: 'Industry', value: 'Cybersecurity', color: '#D1E5FF' },
    {
      label: 'Cost Savings',
      value: '50% lower than U.S. market rates',
      color: '#D1E5FF',
    },
    { label: 'Technology', value: 'Okta, Auth0', color: '#D1E5FF' },
    {
      label: 'Ramp-Up Efficiency',
      value: 'Minimized training costs due to pre-vetted, project-ready talent',
      color: '#FFD1D1',
    },
  ],
  challengeContent: {
    title: 'The Challenge',
    description: [
      'Anthropic Identity delivers enterprise-level Identity and Access Management (IAM) implementation services with the Okta and Auth0 platforms. As the company scaled, it faced three critical challenges:',
    ],
    roadblocks: [
      'Trust Issues: Many freelance platforms lack transparency in vetting IAM experts. Verifying credentials, past experience, and security practices was difficult, leading to concerns about reliability, expertise, and accountability.',
      'Slow Hiring Cycles: Recruiting top IAM engineers took 12–16 weeks, a timeline they couldn’t afford.',
      'High Costs: U.S IAM salaries range from $150K–$250K, making it unsustainable for a startup.',
      'Niche Expertise Required: Finding engineers with OAuth, OpenID, and Okta expertise and a security-first mindset proved challenging.',
      'Compliance & Security Issues: IAM is a highly regulated sector, and hiring freelance IAM advisors introduced potential security and compliance concerns, including data privacy risks and lack of adherence to industry standards.',
    ],
    quoteBackgroundColor: '#f0f4ff',
    order: 3,
    showQuote: false,
    showQuoteAuthor: false,
  },
  solutionContent: {
    title: 'The Solution',
    subtitle: 'DIGU’s Staff Augmentation Service',
    description: [
      'Anthropic partnered with DIGU to establish a scalable, cost-effective delivery model. DIGU took full ownership of training IAM professionals before introducing them to the Anthropic team. These professionals came interview-ready, equipped with relevant certifications and real-world preparation.',
    ],
    benefitTitle: 'The partnership includes:',
    benefits: [
      'Pre-Trained Talent:  with expertise in Okta/Auth0',
      'On-Demand Deployment: of engineers aligned with Anthropic’s project timelines',
      'Global Support Coverage: enabling 24/7 service delivery',
      'Reduced Overhead Costs: while maintaining high service quality',
      'Cultural Alignment and Communication Readiness: for seamless collaboration',
    ],
    showBenefitTitle: true,
  },
  resultsContent: {
    title: 'The Results',
    description:
      'While still in the early stages of the partnership, Anthropic Identity is already seeing foundational benefits:',
    outcomes: [
      'Faster hiring cycles:  with access to IAM-trained professionals',
      'Improved project readiness: thanks to DIGU’s enablement-first approach',
      'Increased flexibility: allowing their team to scale up as new client opportunities arise',
      'Cost-effective delivery: without compromising on technical depth or customer experience',
      'Global service capabilitie: positioning Anthropic to confidently serve international clients',
    ],
    summary:
      "As the partnership evolves, DIGU will continue supporting Anthropic's growth—helping them focus on delivering secure, human-centric identity solutions at scale.",
    watermarkText: 'DIVACS',
    divacsColor: '#2196f3',
    showDescription: true,
  },
  impactContent: {
    title: 'Impact Beyond Business: Empowering Nepal’s Tech Ecosystem',
    description:
      'DIGU is one of the most trusted and reliable professional IT service firms, delivering scalable, cost-effective solutions for SaaS startups and enterprises. However, our impact extends beyond business. Through the DIGU Grassroots Impact Initiative, every hire and project drives real change in Nepal’s tech community.',
    meterDescription:
      'Our Impact Meter provides clear insights into the measurable difference that DIVACS and DIGU have created in the Nepalese community.',
    categories: [
      {
        title: 'Job Creation',
        metrics: [{ label: 'Professionals Hired by Anthropic', value: 1 }],
      },
      {
        title: 'Professional Development Programs',
        metrics: [
          {
            label: 'Resume-Building Workshop Sponsored (10 People)',
            value: 1,
          },
        ],
      },
      {
        title: 'Overall Grassroot Impact',
        highlightHeader: true,
        metrics: [
          { label: 'Families Supported', value: 1 },
          { label: 'Skilled Professionals Retained in Nepal', value: 1 },
        ],
      },
    ],
    showMeterDescription: false,
  },
  talentTestimonialContent: {
    title: 'Talent',
    testimonials: [
      {
        name: 'Ankit Kumar Sha',
        title: 'IAM Advisor at Anthropic Identity',
        hasImage: true,
        message:
          'I was just another skilled developer looking for opportunities when DIGU connected me with Anthropic Identity. They didn’t just help me land a job. They prepared me for it. From Okta certification sponsorship to mentorship on industry best practices, DIGU made sure I was ready to excel. Today, I’m working as IAM advisor at Anthropic Identity and I owe it all to DIGU’s guidance. If you’re an engineer in Nepal looking for global opportunities, this is the team that will get you there.',
      },
    ],
  },
  customerTestimonialContent: {
    title: 'Founder',
    testimonial: {
      name: 'James Bonifield',
      title: 'Founder & CEO DIVACS, Inc.',
      message:
        'We are thrilled to partner with the team at DIGU. This collaboration accelerates our mission to deliver human-centric identity solutions, bridging product, development, and security. More importantly, it empowers a company that’s making a meaningful impact in so many lives.',
      image: require('@/assets/img/james_bonifield.webp'),
      linkedin: 'https://www.linkedin.com/in/bonifield/',
    },
  },
};
